import { createTheme } from '@mui/material/styles';
import { FW, FW_SHADOW, LS, LS_SHADOW, WHW, WHW_SHADOW } from './tokens';

export const THEME_PRESETS = [
  {
    id: 'fabworks',
    label: 'Fab Works (New)',
    description: 'Steel and navy industrial look, matching the Outside Sales Portal.',
  },
  {
    id: 'langley',
    label: 'Langley Services',
    description: 'Black, white, and warm grays — clean neutral UI.',
  },
  {
    id: 'whw',
    label: 'WHW Industrial',
    description: 'Charcoal and deep red — matching the WHW Industrial Solutions mark.',
  },
  {
    id: 'classic',
    label: 'Classic',
    description: 'The original Material blue theme WorkManager shipped with.',
  },
];

/** Presets that use the modern branded sidebar and dashboard styling. */
export const BRANDED_THEME_PRESETS = ['fabworks', 'langley', 'whw'];

/** Branded presets with a light sidebar (logo sits on white). */
export const LIGHT_NAV_PRESETS = ['langley', 'whw'];

export const isLightNavPreset = (value) => LIGHT_NAV_PRESETS.includes(value);

export const isBrandedPreset = (value) => BRANDED_THEME_PRESETS.includes(value);

/** Opt-in: the app keeps its original look until an Admin switches the theme. */
export const DEFAULT_THEME_PRESET = 'classic';

export const isValidPreset = (value) => THEME_PRESETS.some((p) => p.id === value);

/** Density overrides both presets share — the app depends on this compact rhythm. */
const denseComponents = {
  MuiButton: {
    styleOverrides: {
      root: { textTransform: 'none', padding: '4px 12px' },
      sizeSmall: { padding: '2px 8px' },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { padding: '8px 12px' },
      body: { '&:first-of-type': { fontWeight: 600 } },
      head: { padding: '8px 12px', fontWeight: 600 },
      sizeSmall: { padding: '4px 8px' },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: { '&:last-child td': { borderBottom: 0 } },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '12px !important',
        '&:last-child': { paddingBottom: '12px !important' },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { padding: '8px' },
    },
  },
  MuiTextField: {
    styleOverrides: { root: { marginBottom: '8px' } },
    defaultProps: { margin: 'dense', size: 'small' },
  },
  MuiToolbar: {
    styleOverrides: {
      root: { minHeight: '48px !important', padding: '0 12px !important' },
      dense: { minHeight: '40px !important' },
    },
  },
  MuiGrid: {
    styleOverrides: { root: { marginTop: '0 !important' } },
  },
  MuiChip: {
    styleOverrides: { root: { height: '24px', fontSize: '0.75rem' } },
  },
  MuiTabs: {
    styleOverrides: { root: { minHeight: '40px' } },
  },
  MuiTab: {
    styleOverrides: { root: { minHeight: '40px', padding: '8px 16px', fontSize: '0.875rem' } },
  },
};

const merge = (base, extra) => {
  const out = { ...base };
  Object.entries(extra).forEach(([key, value]) => {
    out[key] = {
      ...(base[key] || {}),
      ...value,
      styleOverrides: {
        ...(base[key]?.styleOverrides || {}),
        ...(value.styleOverrides || {}),
      },
      defaultProps: {
        ...(base[key]?.defaultProps || {}),
        ...(value.defaultProps || {}),
      },
    };
  });
  return out;
};

const classicTheme = () =>
  createTheme({
    fw: { preset: 'classic', tokens: FW },
    palette: {
      mode: 'light',
      primary: { main: '#1976d2', contrastText: '#ffffff' },
      secondary: { main: '#dc004e', contrastText: '#ffffff' },
      background: { default: '#ffffff', paper: '#ffffff' },
      // Neutrals for filter bars, sub-table headers, and highlighted panels
      surfaceMuted: { main: '#f5f5f5' },
      surfaceAccent: { main: '#e3f2fd' },
      borderSubtle: { main: '#e0e0e0' },
    },
    typography: { fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
    spacing: 6,
    components: merge(denseComponents, {
      MuiCard: {
        styleOverrides: { root: { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' } },
      },
      MuiAppBar: {
        styleOverrides: { root: { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' } },
      },
    }),
  });

const fabworksTheme = () =>
  createTheme({
    fw: { preset: 'fabworks', branded: true, tokens: FW },
    palette: {
      mode: 'light',
      primary: { main: FW.steel, dark: FW.steelDark, light: FW.steelSoft, contrastText: FW.white },
      secondary: { main: FW.accent, contrastText: FW.white },
      success: { main: FW.success },
      warning: { main: FW.warning },
      error: { main: FW.danger },
      info: { main: FW.info },
      text: { primary: FW.ink, secondary: FW.muted },
      divider: FW.line,
      background: { default: FW.surface, paper: FW.white },
      surfaceMuted: { main: FW.subtle },
      surfaceAccent: { main: FW.steelSoft },
      borderSubtle: { main: FW.line },
    },
    typography: {
      fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      h4: { fontWeight: 700, letterSpacing: -0.3 },
      h5: { fontWeight: 700, letterSpacing: -0.2 },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
    },
    spacing: 6,
    shape: { borderRadius: 8 },
    components: merge(denseComponents, {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: FW.surface, color: FW.ink },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { padding: '8px', backgroundImage: 'none' },
          elevation1: { boxShadow: FW_SHADOW.card },
          outlined: { border: `1px solid ${FW.line}` },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${FW.line}`,
            borderRadius: 10,
            boxShadow: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: 'none', borderBottom: `1px solid ${FW.line}`, backgroundImage: 'none' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundImage: 'none', borderRadius: 0 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { padding: '8px 12px', borderBottomColor: FW.line },
          body: { '&:first-of-type': { fontWeight: 600 } },
          head: {
            padding: '8px 12px',
            backgroundColor: FW.subtle,
            color: FW.muted,
            fontWeight: 700,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            borderBottom: `1px solid ${FW.line}`,
          },
          sizeSmall: { padding: '4px 8px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': { borderBottom: 0 },
            '&.MuiTableRow-hover:hover': { backgroundColor: FW.steelSoft },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', padding: '4px 12px', borderRadius: 8, fontWeight: 600 },
          sizeSmall: { padding: '2px 8px' },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: { borderColor: FW.line },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { height: '24px', fontSize: '0.75rem', fontWeight: 600, borderRadius: 6 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: FW.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: FW.steel },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: '40px' },
          indicator: { backgroundColor: FW.steel },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: '40px',
            padding: '8px 16px',
            fontSize: '0.875rem',
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': { color: FW.steel },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${FW.line}` },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 700, color: FW.ink } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: FW.navy, fontSize: '0.75rem' },
          arrow: { color: FW.navy },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
    }),
  });

const langleyTheme = () =>
  createTheme({
    fw: { preset: 'langley', branded: true, lightNav: true, tokens: LS },
    palette: {
      mode: 'light',
      primary: { main: LS.ink, dark: LS.black, light: LS.subtle, contrastText: LS.white },
      secondary: { main: LS.muted, contrastText: LS.white },
      success: { main: LS.success },
      warning: { main: LS.warning },
      error: { main: LS.danger },
      info: { main: LS.info },
      text: { primary: LS.ink, secondary: LS.muted },
      divider: LS.line,
      background: { default: LS.surface, paper: LS.white },
      surfaceMuted: { main: LS.subtle },
      surfaceAccent: { main: LS.subtle },
      borderSubtle: { main: LS.line },
    },
    typography: {
      fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      h4: { fontWeight: 700, letterSpacing: -0.3 },
      h5: { fontWeight: 700, letterSpacing: -0.2 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
    },
    spacing: 6,
    shape: { borderRadius: 8 },
    components: merge(denseComponents, {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: LS.surface, color: LS.ink },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { padding: '8px', backgroundImage: 'none' },
          elevation1: { boxShadow: LS_SHADOW.card },
          outlined: { border: `1px solid ${LS.line}` },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${LS.line}`,
            borderRadius: 10,
            boxShadow: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: 'none', borderBottom: `1px solid ${LS.line}`, backgroundImage: 'none' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundImage: 'none', borderRadius: 0 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { padding: '8px 12px', borderBottomColor: LS.line },
          body: { '&:first-of-type': { fontWeight: 600 } },
          head: {
            padding: '8px 12px',
            backgroundColor: LS.subtle,
            color: LS.muted,
            fontWeight: 700,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            borderBottom: `1px solid ${LS.line}`,
          },
          sizeSmall: { padding: '4px 8px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': { borderBottom: 0 },
            '&.MuiTableRow-hover:hover': { backgroundColor: LS.subtle },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', padding: '4px 12px', borderRadius: 8, fontWeight: 600 },
          sizeSmall: { padding: '2px 8px' },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: { borderColor: LS.line },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { height: '24px', fontSize: '0.75rem', fontWeight: 600, borderRadius: 6 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: LS.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: LS.muted },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: '40px' },
          indicator: { backgroundColor: LS.ink },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: '40px',
            padding: '8px 16px',
            fontSize: '0.875rem',
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': { color: LS.ink },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${LS.line}` },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 700, color: LS.ink } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: LS.black, fontSize: '0.75rem' },
          arrow: { color: LS.black },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
    }),
  });

const whwTheme = () =>
  createTheme({
    fw: { preset: 'whw', branded: true, lightNav: true, tokens: WHW },
    palette: {
      mode: 'light',
      primary: { main: WHW.red, dark: WHW.redDark, light: WHW.redSoft, contrastText: WHW.white },
      secondary: { main: WHW.ink, contrastText: WHW.white },
      success: { main: WHW.success },
      warning: { main: WHW.warning },
      error: { main: WHW.danger },
      info: { main: WHW.info },
      text: { primary: WHW.ink, secondary: WHW.muted },
      divider: WHW.line,
      background: { default: WHW.surface, paper: WHW.white },
      surfaceMuted: { main: WHW.subtle },
      surfaceAccent: { main: WHW.redSoft },
      borderSubtle: { main: WHW.line },
    },
    typography: {
      fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      h4: { fontWeight: 700, letterSpacing: -0.3 },
      h5: { fontWeight: 700, letterSpacing: -0.2 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
    },
    spacing: 6,
    shape: { borderRadius: 8 },
    components: merge(denseComponents, {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: WHW.surface, color: WHW.ink },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { padding: '8px', backgroundImage: 'none' },
          elevation1: { boxShadow: WHW_SHADOW.card },
          outlined: { border: `1px solid ${WHW.line}` },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${WHW.line}`,
            borderRadius: 10,
            boxShadow: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: 'none', borderBottom: `1px solid ${WHW.line}`, backgroundImage: 'none' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundImage: 'none', borderRadius: 0 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { padding: '8px 12px', borderBottomColor: WHW.line },
          body: { '&:first-of-type': { fontWeight: 600 } },
          head: {
            padding: '8px 12px',
            backgroundColor: WHW.subtle,
            color: WHW.muted,
            fontWeight: 700,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            borderBottom: `1px solid ${WHW.line}`,
          },
          sizeSmall: { padding: '4px 8px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': { borderBottom: 0 },
            '&.MuiTableRow-hover:hover': { backgroundColor: WHW.subtle },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', padding: '4px 12px', borderRadius: 8, fontWeight: 600 },
          sizeSmall: { padding: '2px 8px' },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: { borderColor: WHW.line },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { height: '24px', fontSize: '0.75rem', fontWeight: 600, borderRadius: 6 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: WHW.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: WHW.muted },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: '40px' },
          indicator: { backgroundColor: WHW.red },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: '40px',
            padding: '8px 16px',
            fontSize: '0.875rem',
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': { color: WHW.red },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${WHW.line}` },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 700, color: WHW.ink } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: WHW.black, fontSize: '0.75rem' },
          arrow: { color: WHW.black },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
    }),
  });

export function createAppTheme(preset) {
  if (preset === 'classic') return classicTheme();
  if (preset === 'langley') return langleyTheme();
  if (preset === 'whw') return whwTheme();
  return fabworksTheme();
}
